import {useCallback, useState} from "react";
import type {Dayjs} from "dayjs";
import {useQueryClient} from "@tanstack/react-query";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {getImproveAPI, USERS_QUERY_KEYS, type ManageDueDatesBody} from "@/app2/shared/data";
import {useImproveAxios} from "@/app2/shared/hooks/useImproveAxios";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import type {ManageDueDatesMode} from "../../Courses/AdminCourseLandingPage/Tabs/BulkEnrollment/components/ManageDueDatesModal";

function formatEnrollmentDueDate(dueDate: Dayjs): string {
	return dueDate.format("YYYY-MM-DD");
}

function buildBody(mode: ManageDueDatesMode, dueDate: Dayjs | null, userId: number): ManageDueDatesBody {
	if (mode === "set" && dueDate) {
		return {userIds: [userId], dueDate: formatEnrollmentDueDate(dueDate)};
	}
	return {userIds: [userId]};
}

export function useManageUserCourseDueDates(userId: number): {
	manageDueDatesForCourses: (
		params: {courseIds: number[]; mode: ManageDueDatesMode; dueDate: Dayjs | null},
		options?: {onSuccess?: () => void}
	) => Promise<void>;
	isPending: boolean;
} {
	const {t} = useSSTranslation();
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const {successSnackbar, warningSnackbar, infoSnackbar} = useCustomSnackbar();
	const {courseEnrollmentsControllerManageDueDates} = getImproveAPI();
	const [isPending, setIsPending] = useState(false);

	const manageDueDatesForCourses = useCallback(
		async (
			params: {courseIds: number[]; mode: ManageDueDatesMode; dueDate: Dayjs | null},
			options?: {onSuccess?: () => void}
		) => {
			setIsPending(true);
			try {
				const {courseIds, mode, dueDate} = params;
				let totalUpdated = 0;
				let totalErrors = 0;

				await Promise.all(
					courseIds.map(async (courseId) => {
						try {
							const response = await courseEnrollmentsControllerManageDueDates(
								courseId,
								buildBody(mode, dueDate, userId),
								axiosConfig
							);
							totalUpdated += response.data.enrollmentIds?.length ?? 0;
							totalErrors += response.data.errors?.length ?? 0;
							queryClient.invalidateQueries({queryKey: ["courses", "enrollments", courseId]});
						} catch {
							totalErrors += 1;
						}
					})
				);

				queryClient.invalidateQueries({queryKey: [USERS_QUERY_KEYS.USER_ACTIVE_COURSES, userId]});
				queryClient.invalidateQueries({queryKey: [USERS_QUERY_KEYS.USER_FEED, userId]});

				if (totalErrors === 0) {
					if (totalUpdated === 0) {
						infoSnackbar(t("manage_due_dates_no_changes", "No changes were needed."));
					} else {
						successSnackbar(
							t("manage_due_dates_success_courses", "Updated due dates for {{count}} course(s)", {
								count: totalUpdated
							})
						);
					}
				} else {
					warningSnackbar(
						t(
							"manage_due_dates_partial_errors",
							"Updated {{updated}} learner(s), {{failed}} failed",
							{updated: totalUpdated, failed: totalErrors}
						)
					);
				}

				options?.onSuccess?.();
			} finally {
				setIsPending(false);
			}
		},
		[
			userId,
			axiosConfig,
			queryClient,
			courseEnrollmentsControllerManageDueDates,
			successSnackbar,
			warningSnackbar,
			infoSnackbar,
			t
		]
	);

	return {
		manageDueDatesForCourses,
		isPending
	};
}
